/**
 * plantHelpMatcher.ts
 *
 * Data-driven plant help matching. Most matches come from PESTS data; the
 * curated plant-care issue list adds high-value non-pest explanations.
 */

import { PESTS } from '@/data/pests';
import type { Pest } from '@/data/pests';
import { PLANT_CARE_ISSUES } from '@/data/plantCareIssues';
import type { PlantCareIssue } from '@/data/plantCareIssues';
import type { WeatherData } from '@/lib/weather';
import { deriveWeatherConditions } from '@/lib/pestMatcher';

// ── Fixed symptom list ─────────────────────────────────────────────────────────
// UI-visible options only. `tags` and `pestTypes` are internal matching hints.
// Adding new pests with matching tags/types requires no changes here.

export interface FixedSymptom {
  id: string;
  label: string;
  /** pest.tags values used for matching. */
  tags: string[];
  /**
   * When set, ALL pests of these types (for the selected plant) are included
   * regardless of tag matching, and receive higher confidence scores than
   * pests that only matched via tags.
   */
  pestTypes?: string[];
}

export const FIXED_SYMPTOMS: FixedSymptom[] = [
  { id: 'yellowing',     label: 'Yellowing leaves',                  tags: ['yellowing'] },
  { id: 'brown-crispy',  label: 'Brown or crispy edges',             tags: ['browning', 'scorched', 'dry', 'spots'] },
  { id: 'spots',         label: 'Spots on leaves',                   tags: ['spots'] },
  { id: 'holes',         label: 'Holes or chewed leaves',            tags: ['holes', 'chewing'] },
  { id: 'powder',        label: 'White or powdery coating',          tags: ['powder'] },
  { id: 'wilt',          label: 'Wilting',                           tags: ['wilt'] },
  { id: 'sticky',        label: 'Sticky residue or visible insects', tags: ['sticky'] },
  { id: 'stunted',       label: 'Slow or stunted growth',            tags: ['stunted', 'slow-growth', 'stunted-growth', 'poor-growth'] },
  { id: 'visible-bugs',  label: 'Bugs or insects on the plant',      tags: ['chewing', 'holes', 'sticky'],
    pestTypes: ['insect', 'animal'] },
  { id: 'fruit-damage',  label: 'Fruit damage',                      tags: ['fruit-damage', 'fruit'] },
  { id: 'mold-rot',      label: 'Mold or rot',                       tags: ['mold', 'rot', 'fungal', 'powder'] },
  { id: 'not-sure',      label: 'Not sure',                          tags: [] },
];

// ── Types ──────────────────────────────────────────────────────────────────────

export type Confidence = 'likely' | 'possible' | 'worth-checking';

export interface HelpMatch {
  kind: 'pest' | 'care-issue';
  pest?: Pest;
  issue?: PlantCareIssue;
  confidence: Confidence;
  weatherRelevant: boolean;
  /** Three-line action guidance derived from pest or plant-care issue data. */
  actionGuidance: string[];
  /** The matched symptom id, if any. */
  matchedSymptomId: string | null;
}

export interface PlantHelpResult {
  matches: HelpMatch[];
}

// ── Action guidance generator ─────────────────────────────────────────────────

const DO_NOTHING_BY_TYPE: Record<string, string> = {
  insect:        'If you only see a few and plants look otherwise healthy, no action is needed right now.',
  disease:       'If the rest of the plant looks healthy, watch closely over the next few days before acting.',
  fungal:        'If growth continues and the affected area is small, watch and wait before treating.',
  animal:        'If damage is minor and not getting worse, monitoring may be enough.',
  deficiency:    'If new growth looks healthy, the issue may be resolving on its own.',
  physiological: 'If the plant continues growing well, this may correct itself without intervention.',
  other:         'If plants look otherwise healthy overall, no action is needed right now.',
};

function firstSentence(text: string): string {
  const dot = text.indexOf('.');
  return dot > 0 ? text.slice(0, dot + 1) : text;
}

function buildActionGuidance(pest: Pest, symptomId: string | null): string[] {
  const isVisibleBugsSymptom = symptomId === 'visible-bugs';
  const isInsectOrAnimal = pest.type === 'insect' || pest.type === 'animal';

  // For "Bugs or insects" + insect-type pests, lead with identification
  // (what it looks like + where to look) rather than a damage description.
  const checkLine = isVisibleBugsSymptom && isInsectOrAnimal && pest.description
    ? firstSentence(pest.description)
    : pest.symptoms[0]
      ? `Look for ${pest.symptoms[0].charAt(0).toLowerCase()}${pest.symptoms[0].slice(1)}.`
      : `Inspect your plant closely for signs of ${pest.name.toLowerCase()}.`;

  const doLine = pest.organicSolutions[0]?.description
    ? firstSentence(pest.organicSolutions[0].description)
    : 'Remove affected material and monitor closely.';

  const waitLine = DO_NOTHING_BY_TYPE[pest.type] ?? DO_NOTHING_BY_TYPE.other;

  return [checkLine, doLine, waitLine];
}

function buildCareIssueActionGuidance(issue: PlantCareIssue): string[] {
  return [
    issue.whatToCheck,
    issue.firstAction,
    issue.whenNotToAct,
  ];
}

// ── Matcher ───────────────────────────────────────────────────────────────────

/**
 * Find pests relevant to a plant and optional symptom selection.
 *
 * When plantNames has more than one entry (i.e. "not sure which plant" mode),
 * all pests matching any of the named plants are included and deduplicated.
 * In that mode all results are capped at 'worth-checking' confidence.
 *
 * When a symptom defines pestTypes, pests of those types are included for
 * the plant even if they don't match tags, and receive priority confidence.
 *
 * Adding a new pest to pests.ts with affectedPlants, tags, and optional
 * weatherTriggers is sufficient for it to appear here — no code changes needed.
 */
export function getPlantHelpMatches({
  plantNames,
  symptomId,
  weather,
  maxResults = 5,
}: {
  plantNames: string[];
  symptomId?: string | null;
  weather?: WeatherData | null;
  maxResults?: number;
}): PlantHelpResult {
  const allPlantsMode = plantNames.length > 1;
  const lowerNames = plantNames.map(n => n.toLowerCase());

  const symptomDef = FIXED_SYMPTOMS.find(s => s.id === symptomId);
  const notSureSymptom = !symptomId || symptomId === 'not-sure';
  const preferredTypes = symptomDef?.pestTypes ?? [];
  const hasPreferredTypes = preferredTypes.length > 0;

  const seen = new Set<string>();
  const candidates: Pest[] = [];

  for (const pest of PESTS) {
    if (seen.has(pest.id)) continue;

    const plantsMatch = pest.affectedPlants.some(ap => lowerNames.includes(ap.toLowerCase()));
    if (!plantsMatch) continue;

    if (!notSureSymptom && symptomDef) {
      // Include if: pest is of a preferred type, OR tags match
      const typeMatch = hasPreferredTypes && preferredTypes.includes(pest.type);
      const tagsMatch = symptomDef.tags.length > 0 && symptomDef.tags.some(t => pest.tags.includes(t));
      if (!typeMatch && !tagsMatch) continue;
    }

    seen.add(pest.id);
    candidates.push(pest);
  }

  const conditions = weather ? deriveWeatherConditions(weather) : new Set<string>();

  const matches: HelpMatch[] = candidates.map(pest => {
    const weatherRelevant = !!pest.weatherTriggers?.some(t => conditions.has(t));
    const isPreferredType = hasPreferredTypes && preferredTypes.includes(pest.type);

    let confidence: Confidence;
    if (allPlantsMode || notSureSymptom) {
      confidence = 'worth-checking';
    } else if (pest.id === 'root-rot' && conditions.has('wet-soil')) {
      confidence = 'likely';
    } else if (isPreferredType && weatherRelevant) {
      // Strong match: correct type + weather supports it
      confidence = 'likely';
    } else if (isPreferredType || weatherRelevant) {
      // Good match: correct type OR weather (not both)
      confidence = 'possible';
    } else {
      // Tag match only — broader category
      confidence = 'worth-checking';
    }

    return {
      kind: 'pest',
      pest,
      confidence,
      weatherRelevant,
      actionGuidance: buildActionGuidance(pest, symptomId ?? null),
      matchedSymptomId: symptomId ?? null,
    };
  });

  for (const issue of PLANT_CARE_ISSUES) {
    const plantsMatch = issue.affectedPlants.some(ap => lowerNames.includes(ap.toLowerCase()));
    if (!plantsMatch) continue;

    if (!notSureSymptom && symptomDef) {
      const tagsMatch = symptomDef.tags.some(tag => issue.symptomTags.includes(tag));
      if (!tagsMatch) continue;
    }

    const wetRootConditions = conditions.has('wet-soil') || conditions.has('rain');
    const confidence: Confidence = allPlantsMode || notSureSymptom
      ? 'worth-checking'
      : wetRootConditions
        ? 'possible'
        : issue.confidence ?? 'likely';

    matches.push({
      kind: 'care-issue',
      issue,
      confidence,
      weatherRelevant: false,
      actionGuidance: buildCareIssueActionGuidance(issue),
      matchedSymptomId: symptomId ?? null,
    });
  }

  const ORDER: Record<Confidence, number> = { likely: 0, possible: 1, 'worth-checking': 2 };
  matches.sort((a, b) => {
    const confidenceDelta = ORDER[a.confidence] - ORDER[b.confidence];
    if (confidenceDelta !== 0) return confidenceDelta;
    if (a.kind !== b.kind) return a.kind === 'care-issue' ? -1 : 1;
    const aName = a.issue?.name ?? a.pest?.name ?? '';
    const bName = b.issue?.name ?? b.pest?.name ?? '';
    return aName.localeCompare(bName);
  });

  return { matches: matches.slice(0, maxResults) };
}
